// A one-month calendar for picking a single day or a span of days.
//
// Click a day for that day alone; press and drag across days for a range. The
// ledger is always scoped to one month, so the calendar shows exactly that
// month and can't wander outside the data the page has loaded.
//
// Presented as a trigger + portaled popover, matching ColumnFilter, so the
// filter panel's grid stays even instead of one cell growing to calendar
// height. The portal escapes the panel's bounds for the same reason.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { colors, spacing, fontSize, fontWeight, radius } from '../styles/theme';

interface Props {
  /** Full year, e.g. 2026. */
  year: number;
  /** 1-indexed month, e.g. 8 for August. */
  month: number;
  /** 'YYYY-MM-DD', or '' for no bound. */
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const iso = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const prettyDay = (d: string) => {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const prettyRange = (from: string, to: string, year: number) => {
  if (!from && !to) return 'All dates';
  if (from && to && from === to) return `${prettyDay(from)} ${year}`;
  if (from && to) return `${prettyDay(from)} – ${prettyDay(to)}`;
  if (from) return `From ${prettyDay(from)}`;
  return `Up to ${prettyDay(to)}`;
};

export default function DayRangeCalendar({ year, month, from, to, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // The in-progress drag. Held locally and committed on mouse-up so the table
  // re-filters once at the end rather than on every day the pointer crosses.
  const [anchor, setAnchor] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ from: string; to: string } | null>(null);

  const active = !!(from || to);
  const shownFrom = draft ? draft.from : from;
  const shownTo = draft ? draft.to : to;

  const daysInMonth = new Date(year, month, 0).getDate();
  const leadingBlanks = new Date(year, month - 1, 1).getDay();

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = 260;
      setCoords({
        top: r.bottom + 4,
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    // capture:true so Escape closes this popover before the workspace's own
    // handler sees it and starts closing the edit row instead.
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  // Listening on the window (not the day cells) so releasing the button outside
  // the calendar still ends the drag, instead of leaving it stuck down.
  useEffect(() => {
    if (!anchor) return;
    const finish = () => {
      setDraft(current => {
        if (current) onChange(current.from, current.to);
        return null;
      });
      setAnchor(null);
    };
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [anchor, onChange]);

  const startDrag = (day: string) => {
    setAnchor(day);
    setDraft({ from: day, to: day });
  };

  const extendDrag = (day: string) => {
    if (!anchor) return;
    setDraft(day < anchor ? { from: day, to: anchor } : { from: anchor, to: day });
  };

  const inRange = (day: string) =>
    !!(shownFrom && shownTo && day >= shownFrom && day <= shownTo);

  const isEnd = (day: string) => day === shownFrom || day === shownTo;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        title={active ? prettyRange(from, to, year) : 'Every day of the month'}
        style={{
          width: '100%', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          padding: '0.45rem 0.6rem',
          borderRadius: radius.sm,
          border: `1px solid ${active || open ? colors.primary : colors.border}`,
          background: active ? colors.primaryBg : colors.surface,
          color: active ? colors.primaryDark : colors.textSecondary,
          fontSize: fontSize.md,
          fontWeight: active ? fontWeight.semibold : fontWeight.medium,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {prettyRange(from, to, year)}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          data-portal-dropdown="true"
          style={{
            position: 'fixed', top: coords.top, left: coords.left, width: 260,
            background: colors.surface, border: `1px solid ${colors.border}`,
            borderRadius: radius.md, boxShadow: '0 10px 24px rgba(15,23,42,0.18)',
            zIndex: 10040, userSelect: 'none',
          }}
        >
          <div style={{
            padding: `${spacing(2)} ${spacing(3)}`, borderBottom: `1px solid ${colors.borderMuted}`,
            fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textPrimary,
          }}>
            {new Date(year, month - 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </div>

          <div style={{ padding: spacing(2) }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
              {WEEKDAYS.map((d, i) => (
                <div key={i} style={{
                  textAlign: 'center', fontSize: '0.65rem', fontWeight: fontWeight.semibold,
                  color: colors.textFaint, padding: '2px 0',
                }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {Array.from({ length: leadingBlanks }, (_, i) => <div key={`b${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const dayNum = i + 1;
                const day = iso(year, month, dayNum);
                const selected = inRange(day) || isEnd(day);
                const end = isEnd(day) && !!shownFrom;
                return (
                  <div
                    key={day}
                    onMouseDown={e => { e.preventDefault(); startDrag(day); }}
                    onMouseEnter={() => extendDrag(day)}
                    style={{
                      textAlign: 'center', padding: '5px 0', cursor: 'pointer',
                      fontSize: fontSize.xs,
                      fontWeight: end ? fontWeight.bold : fontWeight.medium,
                      borderRadius: radius.sm,
                      background: end ? colors.primary : selected ? colors.primaryBg2 : 'transparent',
                      color: end ? '#fff' : selected ? colors.primaryDark : colors.textSecondary,
                    }}
                  >
                    {dayNum}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: spacing(3),
            padding: `${spacing(2)} ${spacing(3)}`, borderTop: `1px solid ${colors.borderMuted}`,
          }}>
            <span style={{ fontSize: '0.68rem' }}>
              {active ? prettyRange(from, to, year) : 'Click a day, or drag for a range'}
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => onChange('', '')}
              disabled={!active}
              style={{ ...linkBtn, ...(active ? {} : { color: colors.textFaint, cursor: 'default' }) }}
            >
              Clear
            </button>
            <button type="button" onClick={() => setOpen(false)} style={linkBtn}>Done</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: colors.primary, cursor: 'pointer',
  fontSize: fontSize.xs, fontWeight: fontWeight.semibold, padding: 0, whiteSpace: 'nowrap',
};
